/**
 * useAnswerFeedback.js
 * ─────────────────────────────────────────────────────────────
 * IJ:       IJ-248 · Phase 5E-2 · Query Thread Render Extraction · sub-slice two of three
 * Date:     2026-08-02
 * Umbrella: IJ-076 · Predictive Text Rebuild continuation · Refactor Programme 2026 · Phase 5E
 * Owner:    E1
 *
 * Purpose:
 *   Extract the answer-feedback state pair (feedbackByQid · commentDraft) plus
 *   the submitAnswerFeedback async handler out of KnowledgeGraphPage.jsx into
 *   a focused hook. The coming QueryThread.jsx extraction (Phase 5E-3) consumes
 *   this hook's returned API instead of a hand-rolled prop bag.
 *
 * State owned:
 *   - feedbackByQid  { [query_id]: { rating: 1|-1, comment, submitting, submitted_at?, failed_at? } }
 *   - commentDraft   { [query_id]: str }  · in-progress comment text
 *
 * Handler owned:
 *   - submitAnswerFeedback(queryId, queryText, rating, comment, rootCause) · POST /api/feedback/answer
 *
 * Byte-verbatim on every log line, toast message, request body field, and
 * setter payload. Zero behaviour change.
 *
 * Constructor arguments (api · API · toast) are threaded from the parent so
 * the handler body stays character-identical to the pre-extraction version.
 */
import { useState } from 'react';

export default function useAnswerFeedback({ api, API, toast }) {
  const [feedbackByQid, setFeedbackByQid] = useState({});
  const [commentDraft, setCommentDraft] = useState({});

  // Answer feedback — thumbs up/down on final answers
  const submitAnswerFeedback = async (queryId, queryText, rating, comment, rootCause) => {
    if (!queryId) {
      // SPD v5.88.1 (2026-02-15) — hard-fail visibly instead of returning
      // silently. Previously the endpoint would 400 on a missing query_id
      // and the user would never know why nothing was recorded.
      console.warn('[feedback] refused to submit: queryId is empty', { queryText, rating });
      toast.error('Could not record feedback (missing query id — refresh and try again)');
      return;
    }
    setFeedbackByQid(prev => ({ ...prev, [queryId]: { ...(prev[queryId] || {}), rating, submitting: true } }));
    try {
      await api.post(`${API}/feedback/answer`, {
        query_id: queryId,
        rating,
        comment: comment || '',
        query_text: queryText || '',
        root_cause: rating === -1 ? (rootCause || null) : null,
      });
      setFeedbackByQid(prev => ({
        ...prev,
        [queryId]: {
          rating,
          comment: comment || '',
          root_cause: rating === -1 ? (rootCause || null) : null,
          submitting: false,
          submitted_at: Date.now(),  // SPD v5.88.1 — timestamp lets the UI show "Recorded" chip persistently
        },
      }));
      if (rating === 1) toast.success('Thanks — feedback recorded');
      else if (rootCause) toast.success("Thanks — we'll route this to the right agent");
      else toast.success("Thanks — we'll learn from this");
    } catch (e) {
      // SPD v5.88.1 — log every failure so silent-drops become debuggable.
      console.error('[feedback] submit failed', {
        queryId,
        rating,
        status: e?.response?.status,
        detail: e?.response?.data?.detail,
        message: e?.message,
      });
      setFeedbackByQid(prev => ({
        ...prev,
        [queryId]: { ...(prev[queryId] || {}), submitting: false, failed_at: Date.now() },
      }));
      toast.error(e?.response?.data?.detail || 'Failed to record feedback — please retry');
    }
  };

  return {
    feedbackByQid, setFeedbackByQid,
    commentDraft, setCommentDraft,
    submitAnswerFeedback,
  };
}
